import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, ShoppingCart, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShopProduct = ({ cart, updateCart }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  const categoryNames = {
    'signage': 'Signage',
    'acrylic-displays': 'Acrylic Displays',
    'menu-stands': 'Menu Stands',
    'promotional-items': 'Promotional Items',
    'large-format': 'Large Format',
    'fabricated-letters': 'Fabricated Letters',
    'vehicle-branding': 'Vehicle Branding',
    'lightboxes': 'Lightboxes',
  };

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      // Try to fetch from database
      const response = await axios.get(`${API}/shop-products/${productId}`);
      setProduct(response.data);
      
      // Fetch category info
      if (response.data.category_id) {
        try {
          const catResponse = await axios.get(`${API}/shop-categories/${response.data.category_id}`);
          setCategory(catResponse.data);
        } catch (e) {
          setCategory({ id: response.data.category_id, name: categoryNames[response.data.category_id] || 'Products' });
        }
      }
    } catch (error) {
      // Generate placeholder product from ID
      const parts = productId.split('-product-');
      const categoryId = parts[0];
      const productNum = parts[1] || '1';
      const catName = categoryNames[categoryId] || 'Product';

      setProduct({
        id: productId,
        category_id: categoryId,
        name: `${catName} Item ${productNum}`,
        description: `High-quality ${catName.toLowerCase()} product with premium materials and expert craftsmanship. This product is manufactured with precision and attention to detail, ensuring durability and a professional finish that meets the highest industry standards.`,
        base_price: Math.floor(Math.random() * 5000) + 500,
        image: '',
        variants: [
          { name: 'Small', dimensions: '300mm x 200mm', price_add: 0 },
          { name: 'Medium', dimensions: '500mm x 350mm', price_add: 200 },
          { name: 'Large', dimensions: '800mm x 600mm', price_add: 500 },
          { name: 'Extra Large', dimensions: '1200mm x 900mm', price_add: 800 },
        ],
        in_stock: true,
      });
      setCategory({ id: categoryId, name: catName });
    } finally {
      setLoading(false);
    }
  };

  if (loading || !product) {
    return <div className="pt-32 px-6 text-center">Loading...</div>;
  }

  // Handle empty variants array - use Standard as fallback only if no variants exist
  const variants = (product.variants && product.variants.length > 0) 
    ? product.variants 
    : [{ name: 'Standard', dimensions: '', price_add: 0 }];
  const currentVariant = variants[selectedVariant] || variants[0];
  const totalPrice = (product.base_price || 0) + (currentVariant?.price_add || 0);
  
  // Calculate display price: use diamond_price if set, otherwise 10% off
  const getDiamondPrice = () => {
    if (product.diamond_price && product.diamond_price > 0) {
      return product.diamond_price + (currentVariant?.price_add || 0);
    }
    return totalPrice * 0.9;
  };
  
  const displayPrice = user && user.role === 'customer' ? getDiamondPrice() : totalPrice;
  const hasDiscount = user && user.role === 'customer';
  const discountLabel = product.diamond_price ? 'DIAMOND PRICE' : '10% OFF';
  const categoryName = category?.name || 'Products';

  const addToCart = () => {
    const cartItem = {
      id: `${product.id}-${currentVariant.name}`,
      productId: product.id,
      name: product.name,
      variant: currentVariant.name,
      price: displayPrice,
      quantity: quantity,
      image: product.image,
    };

    const existingIndex = cart.findIndex(item => item.id === cartItem.id);
    let newCart;
    if (existingIndex >= 0) {
      newCart = [...cart];
      newCart[existingIndex].quantity += quantity;
    } else {
      newCart = [...cart, cartItem];
    }
    updateCart(newCart);
    toast.success(`Added ${quantity}x ${product.name} (${currentVariant.name}) to cart`);
  };

  return (
    <div className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-6xl mx-auto"
      >
        <Button
          onClick={() => navigate(`/shop/category/${product.category_id || category?.id}`)}
          variant="ghost"
          className="mb-6 text-white/70 hover:text-white"
        >
          <ArrowLeft className="mr-2" size={20} /> Back to {categoryName}
        </Button>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="bg-white/5 border border-white/10 h-[500px] flex items-center justify-center overflow-hidden">
            {product.image ? (
              <img src={product.image} alt="" className="w-full h-full object-contain" />
            ) : (
              <div className="text-white/30 text-center">
                <ShoppingCart size={80} />
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-4xl font-black mb-4">{product.name}</h1>
            <p className="text-white/70 mb-6 whitespace-pre-line">{product.description}</p>

            {/* Variants */}
            {variants.length > 1 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3">Select Size / Variant</h3>
                <div className="grid grid-cols-2 gap-3">
                  {variants.map((variant, index) => (
                    <button
                      key={variant.name}
                      onClick={() => setSelectedVariant(index)}
                      className={`p-4 border transition-all ${
                        selectedVariant === index
                          ? 'border-[#D4AF37] bg-[#D4AF37]/20 text-[#D4AF37]'
                          : 'border-white/20 hover:border-white/40'
                      }`}
                    >
                      <div className="font-bold">{variant.name}</div>
                      {variant.dimensions && <div className="text-sm text-white/60">{variant.dimensions}</div>}
                      {variant.price_add > 0 && (
                        <div className="text-xs text-[#D4AF37] mt-1">+R{variant.price_add}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-3">Quantity</h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 border border-white/20 hover:border-white/40 flex items-center justify-center"
                >
                  <Minus size={16} />
                </button>
                <span className="text-xl font-bold w-12 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 border border-white/20 hover:border-white/40 flex items-center justify-center"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Price */}
            <div className="mb-6">
              {hasDiscount && (
                <p className="text-lg text-white/40 line-through mb-1">R{totalPrice.toFixed(2)}</p>
              )}
              <p className="text-4xl font-black text-[#D4AF37]">
                R{displayPrice.toFixed(2)}
                {hasDiscount && (
                  <span className="ml-3 text-sm bg-[#D4AF37] text-black px-3 py-1 font-bold align-middle">{discountLabel}</span>
                )}
              </p>
              <p className="text-white/50 text-sm mt-1">Total for {quantity} item(s): R{(displayPrice * quantity).toFixed(2)}</p>
            </div>

            {/* Lead time notice - shown above button when product has lead time */}
            {product.in_stock === false && product.lead_time && (
              <div className="p-3 border border-[#D4AF37]/30 bg-[#D4AF37]/10 rounded-lg text-center">
                <p className="text-[#D4AF37] font-semibold">
                  Lead Time: {product.lead_time}
                </p>
              </div>
            )}

            {/* Add to Cart */}
            <Button
              onClick={addToCart}
              className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-bold py-6 text-lg"
              disabled={product.in_stock === false && !product.lead_time}
            >
              <ShoppingCart className="mr-2" /> {product.in_stock === false && !product.lead_time ? 'Out of Stock' : 'Add to Cart'}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ShopProduct;
